import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { trackingId } from '../../../tracking';
import { ActionBarButton } from './action-bar-button';
import { SidePanel, SidePanelType } from './constants';

export interface CallRouteSidebarProps {
  activePanel?: SidePanelType;
  isDeviceExtensionRoute: boolean;
  isSingleTypeScope: boolean;
  callRouteIsLoading: boolean;
  phoneNumbersIsLoading: boolean;
  phoneOverridesIsLoading: boolean;
  unallocatedPhoneNumbersIsLoading: boolean;
  onPanelClick: (panel: SidePanelType) => void;
}

/**
 * Vertical sidebar for call route details page.
 * Displays buttons to switch between different side panels (Numbers, Location, Phone Hours, Override).
 */
export const CallRouteSidebar = ({
  activePanel,
  isDeviceExtensionRoute,
  isSingleTypeScope,
  callRouteIsLoading,
  phoneNumbersIsLoading,
  phoneOverridesIsLoading,
  unallocatedPhoneNumbersIsLoading,
  onPanelClick,
}: CallRouteSidebarProps) => {
  const { t } = useTranslation('phone');

  return (
    <aside
      css={css`
        width: 80px;
        border-left: 1px solid ${theme.colors.neutral10};
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing(1)};
        padding: ${theme.spacing(2)} 0;
      `}
    >
      <ActionBarButton
        iconName='porting-numbers'
        title={t('Numbers')}
        label={SidePanel.Numbers}
        isActive={activePanel === SidePanel.Numbers}
        trackingId={trackingId({
          context: 'setting',
          feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-route',
          details: 'numbers-side-btn',
        })}
        isLoading={callRouteIsLoading || phoneNumbersIsLoading || unallocatedPhoneNumbersIsLoading}
        onClick={() => onPanelClick(SidePanel.Numbers)}
      />
      <ActionBarButton
        iconName='location'
        title={t('Location')}
        label={SidePanel.Location}
        isActive={activePanel === SidePanel.Location}
        trackingId={trackingId({ context: 'setting', feature: 'call-route', details: 'location-side-btn' })}
        onClick={() => onPanelClick(SidePanel.Location)}
        isLoading={callRouteIsLoading}
        shouldHide={isSingleTypeScope || isDeviceExtensionRoute}
      />
      <ActionBarButton
        iconName='clock'
        title={t('Hours')}
        label={SidePanel.PhoneHours}
        isActive={activePanel === SidePanel.PhoneHours}
        trackingId={trackingId({ context: 'setting', feature: 'call-route', details: 'clock-side-btn' })}
        onClick={() => onPanelClick(SidePanel.PhoneHours)}
        isLoading={callRouteIsLoading}
        shouldHide={isDeviceExtensionRoute}
      />
      <ActionBarButton
        iconName='voicemail-override'
        title={t('Override')}
        label={SidePanel.Override}
        isActive={activePanel === SidePanel.Override}
        trackingId={trackingId({
          context: 'setting',
          feature: isDeviceExtensionRoute ? 'device-extension-route' : 'call-route',
          details: 'override-side-btn',
        })}
        isLoading={callRouteIsLoading || phoneOverridesIsLoading}
        onClick={() => onPanelClick(SidePanel.Override)}
      />
    </aside>
  );
};
